import React, {useEffect, useState} from 'react';
import {ReactComponent as OpenFullScreen} from '../../../../assests/svg/openFullScreen.svg';
import {
  Box,
  CssBaseline,
  Grid,
  Typography,
  CircularProgress,
  IconButton,
} from '@mui/material';
import {ThemeProvider, createTheme} from '@mui/material/styles';
import BarChartCell from '../../../reports/HostingVolumeReport/BarChartCell';
import {useSelector} from 'react-redux';
import {RootState} from '../../../../redux/store';
import {t} from 'i18next';
import DocTypesDialog from '../../../pages/welcome-screen-single-client/DocTypesDialog';
import AurDataGridPro from '../../../common/aur-data-grid-pro/AurDataGridPro';
import AurDataGridErrorOverlay from '../../../common/aur-data-grid-error-overlay/AurDataGridErrorOverlay';
import {getDocTypes} from '../../../../api/EcaManagementService';
import {
  GA_CLICK_TYPES,
  GA_EVENT_NAMES,
  GA_REGIONS,
  GA_SUBPAGE_TYPE,
} from '../../../../constants/analyticsConstants';
import {useAnalytics} from '../../../../hooks/useAnalytics';

interface DocType {
  docTypeId: string;
  name: string;
  documentCount: number;
}

interface EcaDocTypesProps {
  loadFileId: number | undefined;
}

type InertDivProps = React.HTMLAttributes<HTMLDivElement> & {
  inert?: string;
};

const hiddenGridProps: InertDivProps = {
  'aria-hidden': true,
  inert: '',
};

const EcaDocTypes: React.FC<EcaDocTypesProps> = ({loadFileId}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isFailedToLoadContent, setIsFailedToLoadContent] = useState(false);
  const [docTypeData, setDocTypeData] = useState<DocType[]>([]);
  const [docTypeCount, setDocTypeCount] = useState(0);

  const {trackGAEvent} = useAnalytics();

  const {projectId, selectedProjectName: projectName} = useSelector(
    (state: RootState) => state.reports
  );
  const clientId = useSelector(
    (state: RootState) => state.selectedClient
  ).clientId;

  const lightTheme = createTheme({
    palette: {
      mode: 'light',
    },
  });

  const sortedRows = [...docTypeData]
    .sort((a, b) => b.documentCount - a.documentCount)
    .slice(0, 5);
  const maxDocumentCount = Math.max(
    ...sortedRows.map((row) => row.documentCount)
  );

  const fetchDocTypes = async () => {
    setLoading(true);
    setIsFailedToLoadContent(false);
    try {
      let paramString;
      if (loadFileId) {
        paramString = `clientId=${clientId}&projectId=${projectId}&loadFilesId=${loadFileId}`;
      } else {
        paramString = `clientId=${clientId}&projectId=${projectId}`;
      }
      const response = await getDocTypes(paramString);
      const res = response.data;
      const data = res?.data || [];
      data.forEach((d: DocType, ind: string) => (d.docTypeId = ind));

      setDocTypeData(data);
      setDocTypeCount(data.length);
    } catch (error) {
      setIsFailedToLoadContent(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDocTypes();
  }, [projectId, loadFileId]);

  const columns = [
    {
      field: 'name',
      headerName: t('common.tableHeaders.name'),
      flex: 1,
      headerClassName: 'no-separator',
      sortable: false,
    },
    {
      field: 'documentsCount',
      headerName: t('common.tableHeaders.documentsCount'),
      flex: 3.5,
      headerClassName: 'no-separator',
      sortable: false,
      renderCell: (params: any) => {
        const rowIndex = params.rowIndex;
        return (
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            width="100%"
          >
            <BarChartCell
              value={params.row.documentCount}
              maxValue={maxDocumentCount}
              name="doctype-value"
              showLabel={true}
              fullWidthBar={true}
              showTooltip={false}
              isFirst={rowIndex === 0}
            />
          </Box>
        );
      },
    },
  ];

  const handleOpen = () => {
    trackGAEvent({
      eventName: GA_EVENT_NAMES.CLICK,
      click: {
        name: t('gaTracking.ecaHomePage.expandWidget'),
        type: GA_CLICK_TYPES.ICON,
        region: GA_REGIONS.WIDGET_BODY,
        action: t('gaTracking.ecaHomePage.modalLabelwidget', {
          label: t('gaTracking.ecaHomePage.docTypeHeading'),
        }),
      },
    });
    setIsOpen(true);
    trackGAEvent({
      eventName: GA_EVENT_NAMES.SUB_PAGE_VIEW,
      subPage: {
        subpage_name: t('gaTracking.ecaHomePage.subpageWidget', {
          label: t('gaTracking.ecaHomePage.docTypeHeading'),
        }),
        subpage_type: GA_SUBPAGE_TYPE.MODAL,
      },
    });
  };

  const handleDocClose = () => {
    setIsOpen(false);
  };

  return (
    <Grid item xs={6}>
      <div className="doc-column">
        <div className="doc-header">
          <div className="d-flex">
            <Typography className="typo-text subhead4_size">
              {t('ecaHomepageModals.docTypeModal.docTypeHeading')} &nbsp;
            </Typography>
            <Typography className="sub-text-count">({docTypeCount})</Typography>
          </div>
          <span id="doc-type-expand-hint" className="sr-only">
            {t('eca.ecaHomepage.ecaDocTypes')}
          </span>

          <IconButton
            size="small"
            onClick={handleOpen}
            className="expand-button"
            aria-label={`${t('ecaHomepageModals.docTypeModal.docTypeHeading')} ${t('buttons.expand')}`}
            aria-describedby='doc-type-expand-hint'
          >
            <OpenFullScreen />
          </IconButton>

          <DocTypesDialog
            isOpen={isOpen}
            projectId={projectId}
            onClose={handleDocClose}
            loadFileId={loadFileId}
            projectName={projectName}
          />
        </div>

        {loading ? (
          <Box
            className="loader-center"
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="300px"
          >
            <CircularProgress />
          </Box>
        ) : (
          <ThemeProvider theme={lightTheme}>
            <CssBaseline />
            <div {...hiddenGridProps}>
            <AurDataGridPro
              rows={sortedRows}
              getRowId={(row) => row.docTypeId}
              columns={columns}
              rowHeight={35}
              hideFooter
              checkBoxVisible={false}
              columnHeaderHeight={0}
              headerFilterHeight={0}
              className="white-table-grid"
              noRowsOverlay={
                isFailedToLoadContent ? AurDataGridErrorOverlay : undefined
              }
              onErrorRefresh={() => fetchDocTypes()}
              disableCellHeaderFocus
            />
            </div>
          </ThemeProvider>
        )}
      </div>
    </Grid>
  );
};

export default EcaDocTypes;
